import type { SurveyListPageDTO } from '@/shared/api/mappers/survey-mapper'
import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  SurveyAnswerCreateRequest,
  SurveyAnswerDTO,
  SurveyAnswersListParams,
  SurveyAnswerUpdateRequest,
  SurveyQuestionCreateRequest,
  SurveyQuestionDTO,
  SurveyQuestionsListParams,
  SurveyQuestionUpdateRequest,
  SurveyResponseDetailDTO,
  SurveyResponsesListParams,
  SurveyResponseSummaryDTO,
  SurveyVersionCreateRequest,
  SurveyVersionDTO,
  SurveyVersionsListParams,
  SurveyVersionUpdateRequest,
} from '@/shared/api/types/survey-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { mapSurveyListPageToPaginated } from '@/shared/api/mappers/survey-mapper'
import { buildLanguageHeaderConfig } from '@/shared/api/utils/language-header'
import { buildTenantHeaderConfig } from '@/shared/api/utils/tenant-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const SURVEY_VERSIONS_PATH = 'admin/surveys/versions'
const SURVEY_QUESTIONS_PATH = 'admin/surveys/questions'
const SURVEY_ANSWERS_PATH = 'admin/surveys/answers'
const SURVEY_RESPONSES_PATH = 'admin/surveys/responses'

export async function listSurveyVersions(
  params: SurveyVersionsListParams,
): Promise<PaginatedResponse<SurveyVersionDTO>> {
  const qs = toQueryString({
    ...(params.skip !== undefined ? { skip: params.skip } : {}),
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
    ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
  })
  const response = await request.get<ApiResponseEnvelope<SurveyListPageDTO<SurveyVersionDTO>>>(
    GW.packaging(qs ? `${SURVEY_VERSIONS_PATH}?${qs}` : SURVEY_VERSIONS_PATH),
    buildLanguageHeaderConfig(params.languageCode),
  )
  const page = unwrapApiEnvelope(response.data)
  return mapSurveyListPageToPaginated(page, params)
}

export async function createSurveyVersion(
  payload: SurveyVersionCreateRequest,
): Promise<ApiResponse<SurveyVersionDTO>> {
  const response = await request.post<ApiResponseEnvelope<SurveyVersionDTO>>(
    GW.packaging(SURVEY_VERSIONS_PATH),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey version created successfully',
  }
}

export async function getSurveyVersionById(
  versionId: string,
): Promise<ApiResponse<SurveyVersionDTO>> {
  const response = await request.get<ApiResponseEnvelope<SurveyVersionDTO>>(
    GW.packaging(`${SURVEY_VERSIONS_PATH}/${versionId}`),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function updateSurveyVersion(
  versionId: string,
  payload: SurveyVersionUpdateRequest,
): Promise<ApiResponse<SurveyVersionDTO>> {
  const response = await request.patch<ApiResponseEnvelope<SurveyVersionDTO>>(
    GW.packaging(`${SURVEY_VERSIONS_PATH}/${versionId}`),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey version updated successfully',
  }
}

export async function deleteSurveyVersion(versionId: string): Promise<ApiResponse<null>> {
  const response = await request.delete<ApiResponseEnvelope<null>>(
    GW.packaging(`${SURVEY_VERSIONS_PATH}/${versionId}`),
  )
  return {
    data: null,
    message: response.data?.message ?? 'Survey version deleted successfully',
  }
}

export async function listSurveyQuestions(
  versionId: string,
  params: SurveyQuestionsListParams,
): Promise<PaginatedResponse<SurveyQuestionDTO>> {
  const qs = toQueryString({
    ...(params.skip !== undefined ? { skip: params.skip } : {}),
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
  })
  const response = await request.get<ApiResponseEnvelope<SurveyListPageDTO<SurveyQuestionDTO>>>(
    GW.packaging(
      qs
        ? `${SURVEY_VERSIONS_PATH}/${versionId}/questions?${qs}`
        : `${SURVEY_VERSIONS_PATH}/${versionId}/questions`,
    ),
    buildLanguageHeaderConfig(params.languageCode),
  )
  const page = unwrapApiEnvelope(response.data)
  return mapSurveyListPageToPaginated(page, params)
}

export async function createSurveyQuestion(
  versionId: string,
  payload: SurveyQuestionCreateRequest,
): Promise<ApiResponse<SurveyQuestionDTO>> {
  const response = await request.post<ApiResponseEnvelope<SurveyQuestionDTO>>(
    GW.packaging(`${SURVEY_VERSIONS_PATH}/${versionId}/questions`),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey question created successfully',
  }
}

export async function getSurveyQuestionById(
  questionId: string,
): Promise<ApiResponse<SurveyQuestionDTO>> {
  const response = await request.get<ApiResponseEnvelope<SurveyQuestionDTO>>(
    GW.packaging(`${SURVEY_QUESTIONS_PATH}/${questionId}`),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function updateSurveyQuestion(
  questionId: string,
  payload: SurveyQuestionUpdateRequest,
): Promise<ApiResponse<SurveyQuestionDTO>> {
  const response = await request.patch<ApiResponseEnvelope<SurveyQuestionDTO>>(
    GW.packaging(`${SURVEY_QUESTIONS_PATH}/${questionId}`),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey question updated successfully',
  }
}

export async function deleteSurveyQuestion(questionId: string): Promise<ApiResponse<null>> {
  const response = await request.delete<ApiResponseEnvelope<null>>(
    GW.packaging(`${SURVEY_QUESTIONS_PATH}/${questionId}`),
  )
  return {
    data: null,
    message: response.data.message ?? 'Survey question deleted successfully',
  }
}

export async function listSurveyAnswers(
  questionId: string,
  params: SurveyAnswersListParams,
): Promise<PaginatedResponse<SurveyAnswerDTO>> {
  const qs = toQueryString({
    ...(params.skip !== undefined ? { skip: params.skip } : {}),
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
  })
  const response = await request.get<ApiResponseEnvelope<SurveyListPageDTO<SurveyAnswerDTO>>>(
    GW.packaging(
      qs
        ? `${SURVEY_QUESTIONS_PATH}/${questionId}/answers?${qs}`
        : `${SURVEY_QUESTIONS_PATH}/${questionId}/answers`,
    ),
    buildLanguageHeaderConfig(params.languageCode),
  )
  const page = unwrapApiEnvelope(response.data)
  return mapSurveyListPageToPaginated(page, params)
}

export async function createSurveyAnswer(
  questionId: string,
  payload: SurveyAnswerCreateRequest,
): Promise<ApiResponse<SurveyAnswerDTO>> {
  const response = await request.post<ApiResponseEnvelope<SurveyAnswerDTO>>(
    GW.packaging(`${SURVEY_QUESTIONS_PATH}/${questionId}/answers`),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey answer created successfully',
  }
}

export async function updateSurveyAnswer(
  answerId: string,
  payload: SurveyAnswerUpdateRequest,
): Promise<ApiResponse<SurveyAnswerDTO>> {
  const response = await request.patch<ApiResponseEnvelope<SurveyAnswerDTO>>(
    GW.packaging(`${SURVEY_ANSWERS_PATH}/${answerId}`),
    payload,
  )
  return {
    data: unwrapApiEnvelope(response.data),
    message: response.data.message ?? 'Survey answer updated successfully',
  }
}

export async function listSurveyResponses(
  tenantId: string,
  params: SurveyResponsesListParams,
): Promise<PaginatedResponse<SurveyResponseSummaryDTO>> {
  const qs = toQueryString({
    ...(params.skip !== undefined ? { skip: params.skip } : {}),
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
  })
  const response = await request.get<
    ApiResponseEnvelope<SurveyListPageDTO<SurveyResponseSummaryDTO>>
  >(
    GW.packaging(qs ? `${SURVEY_RESPONSES_PATH}?${qs}` : SURVEY_RESPONSES_PATH),
    buildTenantHeaderConfig(tenantId),
  )
  const page = unwrapApiEnvelope(response.data)
  return mapSurveyListPageToPaginated(page, params)
}

export async function listSurveyResponsesByUser(
  tenantId: string,
  userId: string,
  params: SurveyResponsesListParams,
): Promise<PaginatedResponse<SurveyResponseDetailDTO>> {
  const qs = toQueryString({
    ...(params.skip !== undefined ? { skip: params.skip } : {}),
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
  })
  const response = await request.get<
    ApiResponseEnvelope<SurveyListPageDTO<SurveyResponseDetailDTO>>
  >(
    GW.packaging(
      qs
        ? `${SURVEY_RESPONSES_PATH}/users/${userId}?${qs}`
        : `${SURVEY_RESPONSES_PATH}/users/${userId}`,
    ),
    buildTenantHeaderConfig(tenantId),
  )
  const page = unwrapApiEnvelope(response.data)
  return mapSurveyListPageToPaginated(page, params)
}
